export default {
  index: {
    title: 'Guías de mareas y corrientes',
    description:
      'Aprende cómo funcionan las mareas y las corrientes de marea: coeficientes de marea, corrientes de flujo y reflujo, planos de referencia de marea (datums) y cómo se calculan las predicciones de marea.',
    subtitle:
      'Explicaciones breves y en lenguaje sencillo sobre la ciencia que hay detrás de las predicciones de mareas y corrientes.',
  },
  back: 'Volver a todas las guías',
  guides: {
    'tidal-coefficients': {
      title: 'Los coeficientes de marea explicados',
      description:
        'Qué significan los coeficientes de marea: la escala de 20 a 120, cómo se relacionan las mareas vivas y muertas con la Luna, y por qué un coeficiente más alto produce una mayor amplitud de marea.',
      blurb:
        'La escala de 20 a 120 que resume cuán grande será una marea y cómo la Luna la impulsa.',
      body: [
        {
          p: 'Un coeficiente de marea es un único número que resume cuán grande será una marea en un día determinado. Se utiliza ampliamente en Francia y en otros países que siguen la tradición hidrográfica francesa. El coeficiente es adimensional y normalmente va de aproximadamente 20 a 120: cuanto mayor es el número, mayor es la amplitud entre la pleamar y la bajamar, y más fuertes son las corrientes de marea asociadas.',
        },
        { h2: 'La escala de 20 a 120' },
        {
          p: 'El coeficiente compara la amplitud de marea del día con una referencia a largo plazo. Varios puntos de la escala tienen significados convencionales:',
        },
        {
          list: [
            {
              term: '20',
              text: 'las mareas más pequeñas, cerca de la amplitud astronómica mínima.',
            },
            { term: '45', text: 'una marea muerta media, cuando la amplitud es pequeña.' },
            {
              term: '70',
              text: 'una marea media, aproximadamente el punto intermedio entre mareas muertas y vivas.',
            },
            { term: '95', text: 'una marea viva media, cuando la amplitud es grande.' },
            {
              term: '120',
              text: 'las mareas más grandes, cerca de la amplitud astronómica máxima.',
            },
          ],
        },
        { h2: 'Por qué cambia: la Luna' },
        {
          p: 'Los coeficientes suben y bajan según el ciclo de mareas vivas y muertas, de aproximadamente dos semanas. Las mareas vivas, con coeficientes altos, se producen uno o dos días después de cada Luna nueva y llena, cuando el Sol y la Luna se alinean y sus fuerzas de marea se suman. Las mareas muertas, con coeficientes bajos, se producen cerca del cuarto creciente y el cuarto menguante, cuando el Sol y la Luna forman un ángulo recto y se cancelan parcialmente.',
        },
        {
          p: 'La distancia de la Luna también influye. Cuando una marea viva coincide con el perigeo (el punto más cercano de la Luna a la Tierra), el coeficiente alcanza sus valores más altos. Las mareas más grandes tienden a agruparse cerca de los equinoccios de primavera y otoño.',
        },
        { h2: 'Lo que un coeficiente no te dice' },
        {
          p: 'El coeficiente describe únicamente la amplitud astronómica. No incluye el efecto del tiempo meteorológico: el viento y la presión atmosférica pueden elevar o reducir el nivel real del agua independientemente del coeficiente. Además, es una propiedad de la marea en su conjunto para ese día, no una altura en metros o pies en un lugar concreto.',
        },
      ],
    },
    'tidal-currents': {
      title: 'Flujo, reflujo y estoa: cómo funcionan las corrientes de marea',
      description:
        'Cómo funcionan las corrientes de marea: la dirección del flujo y el reflujo, la estoa en el momento del cambio, la velocidad de la corriente en nudos y su dirección (set), y por qué la estoa no siempre coincide con la pleamar y la bajamar.',
      blurb:
        'El flujo, el reflujo y la estoa, y por qué la corriente más fuerte no siempre se da en la pleamar.',
      body: [
        {
          p: 'Las corrientes de marea son el movimiento horizontal del agua que acompaña al ascenso y descenso vertical de la marea. Mientras que la altura de la marea indica la profundidad del agua, la corriente indica en qué dirección fluye y a qué velocidad. Las corrientes son más importantes en canales, entradas y estuarios, donde pueden alcanzar varios nudos.',
        },
        { h2: 'Flujo, reflujo y estoa' },
        {
          list: [
            {
              term: 'Corriente de flujo',
              text: 'agua que fluye hacia la costa o remontando un estuario, generalmente mientras la marea sube.',
            },
            {
              term: 'Corriente de reflujo',
              text: 'agua que fluye hacia el mar o descendiendo un estuario, generalmente mientras la marea baja.',
            },
            {
              term: 'Estoa',
              text: 'la breve pausa en la que la corriente es casi nula al cambiar entre flujo y reflujo.',
            },
          ],
        },
        {
          p: 'La velocidad de la corriente se mide en nudos (millas náuticas por hora). Su dirección, llamada set, se expresa como un rumbo de la brújula: la dirección hacia la que fluye el agua.',
        },
        { h2: 'La estoa no siempre coincide con la pleamar' },
        {
          p: 'Una suposición habitual es que la corriente se detiene en la pleamar y la bajamar. Eso solo es cierto en algunos lugares. El momento depende de cómo se comporta la onda de marea localmente. Donde la marea actúa como una onda progresiva, como en un canal largo y abierto, el flujo y el reflujo más fuertes se producen cerca de la pleamar y la bajamar, y la estoa cae cerca del punto medio. Donde actúa como una onda estacionaria, como en el fondo de una bahía cerrada, ocurre lo contrario: la corriente más rápida se da cerca de la media marea y la estoa se produce cerca de la pleamar y la bajamar. Las tablas de corrientes locales son la guía fiable.',
        },
        { h2: 'Corrientes alternativas y giratorias' },
        {
          p: 'En los canales estrechos la corriente suele ser alternativa: fluye en un sentido durante el flujo, luego cambia y regresa durante el reflujo. En aguas costeras abiertas la corriente suele ser giratoria, cambiando de dirección continuamente por toda la brújula a lo largo de un ciclo de marea en lugar de invertirse a lo largo de una única línea.',
        },
      ],
    },
    'tidal-datums': {
      title: 'Planos de referencia de marea y alturas de marea',
      description:
        'Desde dónde se mide una altura de marea: el cero hidrográfico, la bajamar astronómica más baja (LAT), la bajamar media inferior (MLLW), y por qué los planos de referencia difieren entre países.',
      blurb:
        'El cero hidrográfico, LAT, MLLW, y qué significa realmente el número de una altura de marea.',
      body: [
        {
          p: 'Toda altura de marea es una medida por encima de un nivel de referencia llamado plano de referencia (datum). Cuando una tabla de mareas dice que el agua alcanzará 1.8 metros, significa 1.8 metros por encima del plano de referencia de marea local, no por encima del fondo marino ni por encima del nivel del mar en el sentido cotidiano. Conocer el plano de referencia es lo que da sentido a una altura.',
        },
        { h2: 'Qué es un plano de referencia de marea' },
        {
          p: 'Un plano de referencia de marea es una elevación de referencia definida por una fase concreta de la marea, promediada durante un largo periodo, convencionalmente unos 19 años, lo que suaviza el ciclo de 18.6 años de la órbita de la Luna. Como es un promedio de observaciones reales, un plano de referencia es específico de un lugar y de la autoridad que lo calculó.',
        },
        { h2: 'El cero hidrográfico y las referencias comunes' },
        {
          p: 'Las cartas náuticas utilizan un plano de referencia de bajamar para que la profundidad real sea casi siempre al menos la profundidad indicada en la carta. Este cero hidrográfico es el cero desde el que se cuentan tanto las profundidades cartografiadas como las alturas de marea. Distintos países adoptan planos de referencia diferentes:',
        },
        {
          list: [
            {
              term: 'LAT (bajamar astronómica más baja)',
              text: 'el nivel más bajo predecible con un tiempo meteorológico medio; utilizado por el Reino Unido y muchas otras naciones.',
            },
            {
              term: 'MLLW (bajamar media inferior)',
              text: 'el promedio de la más baja de las dos bajamares de cada día; el cero hidrográfico estándar en los Estados Unidos.',
            },
            {
              term: 'MSL (nivel medio del mar)',
              text: 'el nivel medio del agua a largo plazo, utilizado principalmente como referencia para las elevaciones del terreno más que para las cartas.',
            },
          ],
        },
        {
          p: 'Los planos de referencia de pleamar, como la pleamar media (MHW) y la pleamar media superior (MHHW), se utilizan con el propósito opuesto, por ejemplo para definir una línea de costa o el gálibo de un puente.',
        },
        { h2: 'Por qué las alturas no son directamente comparables' },
        {
          p: 'Como los planos de referencia difieren, una altura en una estación no puede compararse directamente con una altura en otra a menos que ambas usen la misma referencia. Una bajamar de 0.5 metros medida desde LAT no es el mismo nivel de agua que una bajamar de 0.5 metros medida desde MLLW. Por eso también la misma marea física puede describirse con números diferentes en las tablas de distintos países.',
        },
      ],
    },
    'how-tide-predictions-work': {
      title: 'Cómo se calculan las predicciones de marea',
      description:
        'Cómo se predicen las mareas: el forzamiento gravitatorio de la Luna y el Sol, las componentes armónicas como M2 y S2, y por qué los niveles de agua observados difieren de la predicción astronómica.',
      blurb:
        'El análisis armónico, las componentes que hay detrás de la marea, y por qué el tiempo meteorológico hace que la realidad difiera.',
      body: [
        {
          p: 'Las mareas pueden predecirse con años de antelación porque están impulsadas por los movimientos de la Luna y el Sol, que se conocen con gran precisión. La marea en un lugar es la respuesta del océano a ese forzamiento gravitatorio, modelada por la costa y la profundidad locales. El método que convierte la astronomía en una tabla de mareas se llama análisis armónico.',
        },
        { h2: 'La marea como suma de componentes' },
        {
          p: 'El análisis armónico trata la marea como la suma de muchas ondas simples, cada una llamada componente. Cada componente tiene una frecuencia fijada por la astronomía: cómo se mueven la Tierra, la Luna y el Sol entre sí: , mientras que su amplitud y su fase se determinan ajustando las componentes a un periodo de observaciones reales del nivel del agua en ese lugar. Las principales componentes incluyen:',
        },
        {
          list: [
            {
              term: 'M2',
              text: 'la componente semidiurna lunar principal, de periodo 12.42 horas; normalmente la mayor.',
            },
            {
              term: 'S2',
              text: 'la componente semidiurna solar principal, de periodo 12.00 horas.',
            },
            { term: 'N2', text: 'una componente lunar debida a la órbita elíptica de la Luna.' },
            {
              term: 'K1 y O1',
              text: 'las principales componentes diurnas, que hacen que una pleamar del día sea mayor que la otra en muchos lugares.',
            },
          ],
        },
        {
          p: 'El conocido ciclo de mareas vivas y muertas surge de forma natural de esto: M2 y S2 tienen periodos ligeramente distintos, por lo que entran y salen de fase, reforzándose cada 14.8 días para producir mareas vivas y cancelándose parcialmente para producir mareas muertas.',
        },
        { h2: 'Predecir hacia adelante' },
        {
          p: 'Una vez que se conocen la amplitud y la fase de cada componente para un lugar, predecir la marea es simplemente cuestión de evaluar la suma en cualquier momento futuro. Las frecuencias astronómicas nunca cambian, por lo que el cálculo es determinista y puede ejecutarse con décadas de antelación.',
        },
        { h2: 'Por qué la marea real difiere' },
        {
          p: 'Una predicción es únicamente la marea astronómica. No incluye el tiempo meteorológico. Los vientos fuertes, la presión atmosférica baja o alta, las lluvias intensas y el caudal de los ríos pueden elevar o reducir el nivel real del agua: una marea de tempestad puede añadir un metro o más por encima de la predicción, mientras que las altas presiones y los vientos de tierra pueden mantener el agua muy por debajo de ella. Las predicciones son, por tanto, una excelente guía sobre el momento y la magnitud de la marea, pero el nivel observado ese día puede diferir.',
        },
      ],
    },
  },
}
